import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown, Users } from 'lucide-react';
import PricingCard from '../components/PricingCard';
import { HUBSPOT_800_SESSION, STRIPE_10K } from '../lib/links';

gsap.registerPlugin(ScrollTrigger);

const buildItems = [
    'Brand-new M5 MacBook Air — yours to keep',
    'Pre-consult call — we map your business, your stack, your agent’s identity',
    'Full OpenClaw build — configured, secured, tested before it ships',
    'Up to 3 API integrations connected and verified',
    'Zoom pre-installed — 3-hour live training, screen-shared on your machine',
    'Tony "The Wrench" Clawzini — your on-machine AI mechanic',
    'RoofClaw Community — private Telegram with Vito + Adam (6 months free, then $99/mo)',
    'Winning Wednesday — weekly group webinar, ongoing',
];

const sessionItems = [
    'Your RoofClaw Implementation Roadmap — a step-by-step plan for how AI fits your specific operation',
    'Clarity on which integrations matter most for your business',
    'A realistic picture of what your agent does on day 1, day 30, day 90',
    'Zero pressure. Zero sales pitch. Just strategy.',
];

const faqs = [
    {
        q: 'Why $10,000?',
        a: "You're not buying software. You're buying a dedicated AI employee — configured for your business, installed on your hardware, trained by us, shipped to your door. The machine is yours. The agent is yours. There's no monthly platform fee eating your margin. One price. Full ownership.",
    },
    {
        q: "What's the difference between the $800 session and just buying?",
        a: "The $800 session is for contractors who want a roadmap before they commit. You get a working document — your personalized implementation plan — whether you buy or not. If you buy, the $800 comes off the price. If you don't, you still leave with more clarity than you came in with.",
    },
    {
        q: 'What if something breaks after it ships?',
        a: 'That’s what Tony is for. Your machine ships with a built-in AI mechanic — Tony "The Wrench" Clawzini — who can diagnose and fix most OpenClaw issues from Terminal. For anything beyond Tony’s scope, you have the RoofClaw Community with direct access to Adam and Vito.',
    },
    {
        q: 'Do I need to be technical?',
        a: "No. That's the point. The machine arrives configured. The 3-hour training session walks you through everything on your actual machine, via Zoom screen share. You'll know how to use it before we hang up.",
    },
    {
        q: 'What software can it connect to?',
        a: 'Any roofing software with an API: JobNimbus, Acculynx, CompanyCam, Zuper, HubSpot, QuickBooks, and more. We connect up to 3 integrations in the build. Additional integrations available post-launch.',
    },
];

const PricingPage = () => {
    const rootRef = useRef(null);
    const [openIndex, setOpenIndex] = useState(0);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from('.pricing-element', {
                y: 40,
                opacity: 0,
                duration: 1,
                stagger: 0.08,
                ease: 'power3.out',
                scrollTrigger: { trigger: rootRef.current, start: 'top 80%' },
            });
        }, rootRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={rootRef} className="pt-32 pb-24 bg-[#0A0A14] text-[#F0EFF4] min-h-screen">
            {/* Headline */}
            <div className="max-w-5xl mx-auto px-6 text-center mb-16">
                <span className="pricing-element font-data text-[#7B61FF] text-xs font-bold tracking-widest uppercase mb-6 block">
                    Pricing
                </span>
                <h1 className="pricing-element text-[#F0EFF4] font-bold text-5xl md:text-7xl tracking-tight mb-6 leading-[1.05]">
                    Two Ways In.<br />
                    <span className="font-drama italic text-[#7B61FF] font-normal">One Destination.</span>
                </h1>
                <p className="pricing-element text-[#F0EFF4]/70 text-lg md:text-xl max-w-2xl mx-auto">
                    Your AI employee is waiting. The only question is how ready you are to meet them.
                </p>
            </div>

            {/* Pricing Cards */}
            <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-20">
                <div className="pricing-element h-full">
                    <PricingCard
                        badge="Ready to Go"
                        badgeVariant="plasma"
                        price="$10,000"
                        priceNote="One-time · USD"
                        headline="I'm Ready. Build Mine."
                        subtext="You know what you want. You've done the research. You're not here to be convinced — you're here to get started."
                        itemsLabel="What's included"
                        items={buildItems}
                        ctaLabel="Build Mine — $10,000"
                        ctaHref={STRIPE_10K}
                        ctaVariant="plasma"
                        ctaTarget="_blank"
                        footnote="Ships from Kelowna, BC. We pay shipping. Continental US & Canada."
                        highlighted
                    />
                </div>
                <div className="pricing-element h-full">
                    <PricingCard
                        badge="Not Sure Yet"
                        badgeVariant="graphite"
                        price="$800"
                        priceNote="60-minute session · USD"
                        headline="Is RoofClaw Right for Me?"
                        subtext="One hour with Adam Sand. We look at your business, your software, your biggest operational bottlenecks — and we build your personalized implementation roadmap together."
                        itemsLabel="What you walk away with"
                        items={sessionItems}
                        offerNote="If you decide to move forward with a RoofClaw build, the $800 applies to your purchase price."
                        ctaLabel="Book Your AI Readiness Session — $800"
                        ctaHref={HUBSPOT_800_SESSION}
                        ctaVariant="outline"
                        ctaTarget="_blank"
                        footnote="60 minutes. Booked instantly. Roadmap delivered same day."
                    />
                </div>
            </div>

            {/* Community Strip */}
            <div className="pricing-element max-w-5xl mx-auto px-6 mb-20">
                <div className="rounded-[2rem] border border-[#7B61FF]/30 bg-[#18181B] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
                    <div className="w-14 h-14 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/30 flex items-center justify-center shrink-0">
                        <Users size={24} className="text-[#7B61FF]" />
                    </div>
                    <div className="flex-1">
                        <p className="font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-2">
                            $99/mo · 6 months free with every build
                        </p>
                        <h3 className="text-[#F0EFF4] text-xl md:text-2xl font-bold mb-2">
                            The RoofClaw Community
                        </h3>
                        <p className="text-[#F0EFF4]/70 text-sm md:text-base leading-relaxed">
                            A private Telegram group where you reach Vito and Adam directly, ask questions, share wins, and get support from other contractors running AI in their businesses. <strong className="text-[#F0EFF4]">Build customers get 6 months free</strong>, then $99/month. Cancel anytime.
                        </p>
                    </div>
                </div>
            </div>

            {/* Tony Callout */}
            <div className="pricing-element max-w-5xl mx-auto px-6 mb-20">
                <div className="rounded-[2rem] border border-[#F0EFF4]/10 bg-[#18181B] p-8 md:p-10 flex flex-col md:flex-row items-start gap-8">
                    <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border border-[#7B61FF]/30 bg-[#0A0A14] shrink-0 shadow-[0_0_30px_rgba(123,97,255,0.15)]">
                        <img
                            src="/images/generated/tony-avatar.png"
                            alt="Tony &quot;The Wrench&quot; Clawzini — RoofClaw's on-machine AI mechanic"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-[#F0EFF4] text-2xl md:text-3xl font-bold mb-3 tracking-tight">
                            Meet Tony. <span className="font-drama italic text-[#7B61FF] font-normal">He Fixes Things.</span>
                        </h3>
                        <p className="text-[#F0EFF4]/70 text-base leading-relaxed mb-3">
                            Every RoofClaw ships with <strong className="text-[#F0EFF4]">Tony "The Wrench" Clawzini</strong> — a separate AI mechanic agent that lives on your machine. When OpenClaw needs an update, when something breaks, when you're not sure what went wrong — open Terminal, type <code className="font-data text-[#7B61FF] text-sm">hermes</code>, and Tony gets to work.
                        </p>
                        <p className="text-[#F0EFF4]/60 text-sm leading-relaxed">
                            Tony runs on his own AI engine, separate from your main agent. So even when your agent is down, your mechanic is up. <span className="text-[#F0EFF4]/40 italic">Usage billed at cost. You only pay when you need him.</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* FAQ */}
            <div className="pricing-element max-w-3xl mx-auto px-6 mb-20">
                <h2 className="text-[#F0EFF4] font-bold text-3xl md:text-4xl tracking-tight mb-8 text-center">
                    Questions.
                </h2>
                <div className="space-y-4">
                    {faqs.map((faq, i) => {
                        const isOpen = openIndex === i;
                        return (
                            <div
                                key={i}
                                className={`group border transition-all duration-500 rounded-2xl overflow-hidden ${
                                    isOpen
                                        ? 'bg-[#18181B] border-[#7B61FF]/40 shadow-[0_0_20px_rgba(123,97,255,0.1)]'
                                        : 'bg-[#0A0A14] border-[#7B61FF]/10 hover:border-[#7B61FF]/30'
                                }`}
                            >
                                <button
                                    onClick={() => setOpenIndex(isOpen ? -1 : i)}
                                    className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                                >
                                    <span className="font-bold text-base md:text-lg pr-6">{faq.q}</span>
                                    <ChevronDown
                                        className={`w-5 h-5 text-[#7B61FF] shrink-0 transition-transform duration-300 ${
                                            isOpen ? 'rotate-180' : ''
                                        }`}
                                    />
                                </button>
                                <div
                                    className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${
                                        isOpen ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0'
                                    }`}
                                >
                                    <p className="text-[#F0EFF4]/70 leading-relaxed border-t border-[#7B61FF]/20 pt-4 text-sm md:text-base">
                                        {faq.a}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Final CTA */}
            <div className="pricing-element max-w-4xl mx-auto px-6 text-center">
                <h2 className="font-drama italic text-[#F0EFF4] text-4xl md:text-5xl leading-[1.1] mb-4">
                    The roofers who move first win the market.
                </h2>
                <p className="text-[#F0EFF4]/70 text-base md:text-lg max-w-2xl mx-auto mb-10">
                    AI isn't coming to the roofing industry. It's already here. The contractors running it now are getting faster, responding quicker, and closing more — while their competition is still figuring out what a prompt is.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <a
                        href={STRIPE_10K}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-magnetic bg-[#7B61FF] text-[#F0EFF4] px-8 py-4 rounded-full font-semibold tracking-wide shadow-[0_0_20px_rgba(123,97,255,0.4)] hover:shadow-[0_0_40px_rgba(123,97,255,0.65)] transition-all"
                    >
                        Build Mine — $10,000 →
                    </a>
                    <a
                        href={HUBSPOT_800_SESSION}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-magnetic border border-[#F0EFF4]/30 text-[#F0EFF4] px-8 py-4 rounded-full font-semibold tracking-wide hover:bg-[#F0EFF4]/5 transition-all"
                    >
                        Book AI Readiness Session — $800 →
                    </a>
                </div>
            </div>
        </div>
    );
};

export default PricingPage;
